/**
 * «برداشت تومان» operations (Phase 4). Confirmed withdrawals only (drafts awaiting the user's SMS code
 * are not operations). Oldest first. The amount is already RESERVED on the ledger; the admin records
 * the real-world outcome:
 *   COMPLETE (rail + tracking) → reserve consumed to the bank settlement account (once, ever);
 *   REJECT → reserve released back to the user (once, ever; refund and completion are exclusive);
 *   HOLD / RESUME → security hold (no money moves);
 *   MARK_UNCERTAIN → the bank outcome is unknown: money stays reserved and a reconciliation case opens
 *   (never auto-refunded, never auto-completed).
 * Internal facts (rail, first-to-destination, uncertainty) are admin-only; the user sees «در حال انجام».
 * No 72-hour hold exists (OPEN-44): nothing here delays by policy.
 */
import {
  AppContainer,
  Button,
  DataTable,
  formatAmount,
  Ltr,
  PageHeader,
  SelectField,
  StateView,
  TextField,
} from '@hopex/ui';
import { useState } from 'react';
import {
  api,
  dt,
  newIdempotencyKey,
  type Paged,
  type TomanWithdrawal,
  type WithdrawalStatus,
} from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { RAIL, WITHDRAWAL_ACTION, WITHDRAWAL_STATUS } from '../../lib/money-labels.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { TechCode } from '../../lib/ui.tsx';
import { InstrumentReveal } from '../Reveal.tsx';
import { Counter, num, Pager, Tone, UserCell } from './shared.tsx';

type Action = 'COMPLETE' | 'REJECT' | 'HOLD' | 'RESUME' | 'MARK_UNCERTAIN';
const ALLOWED: Record<Action, WithdrawalStatus[]> = {
  COMPLETE: ['PROCESSING', 'SETTLEMENT_UNCERTAIN'],
  REJECT: ['PROCESSING', 'SECURITY_HOLD', 'SETTLEMENT_UNCERTAIN'],
  HOLD: ['PROCESSING'],
  RESUME: ['SECURITY_HOLD'],
  MARK_UNCERTAIN: ['PROCESSING'],
};
const EFFECT: Record<Action, string> = {
  COMPLETE: 'مبلغ رزروشده یک‌بار و برای همیشه به حساب تسویه‌ی بانکی منتقل می‌شود.',
  REJECT: 'مبلغ رزروشده به موجودی قابل استفاده‌ی کاربر برمی‌گردد.',
  HOLD: 'پردازش متوقف می‌شود؛ مبلغ رزرو می‌ماند.',
  RESUME: 'پردازش ادامه پیدا می‌کند؛ مبلغ رزرو می‌ماند.',
  MARK_UNCERTAIN: 'نتیجه‌ی بانکی نامشخص ثبت می‌شود؛ مبلغ رزرو می‌ماند و پرونده‌ی تطبیق باز می‌شود.',
};
const TONE: Record<Action, 'positive' | 'danger' | 'caution'> = {
  COMPLETE: 'positive',
  REJECT: 'danger',
  HOLD: 'caution',
  RESUME: 'positive',
  MARK_UNCERTAIN: 'caution',
};

function ActionDialog({
  w,
  action,
  onClose,
  onDone,
}: {
  w: TomanWithdrawal;
  action: Action;
  onClose: () => void;
  onDone: () => void;
}) {
  const [rail, setRail] = useState('');
  const [tracking, setTracking] = useState('');
  const [key] = useState(newIdempotencyKey);
  return (
    <CommandDialog
      title={`${WITHDRAWAL_ACTION[action]} — ${w.ref}`}
      tone={TONE[action]}
      confirmLabel={WITHDRAWAL_ACTION[action]}
      canSubmit={action !== 'COMPLETE' || rail !== ''}
      run={async (reason) => {
        await api.post(
          `/toman-withdrawals/${w.id}/action`,
          {
            action,
            reason,
            expectedVersion: w.version,
            ...(action === 'COMPLETE' ? { rail } : {}),
            ...(action === 'COMPLETE' && tracking.trim() ? { trackingRef: tracking.trim() } : {}),
          },
          { 'idempotency-key': key },
        );
      }}
      onClose={onClose}
      onDone={onDone}
      data-withdrawal-action={action}
    >
      <Facts
        rows={[
          ['کاربر', w.displayName ?? w.publicId],
          [
            'مبلغ',
            <Ltr key="a" className="font-bold">
              {formatAmount(w.amount, 'TOMAN')}
            </Ltr>,
          ],
          [
            'مقصد',
            <InstrumentReveal
              key="d"
              userId={w.userId}
              instrumentId={w.instrumentId}
              masked={w.destination.masked}
            />,
          ],
          ['وضعیت فعلی', WITHDRAWAL_STATUS[w.status]?.label ?? w.status],
          ['اثر', EFFECT[action]],
        ]}
      />
      {action === 'COMPLETE' && (
        <div className="grid gap-2 sm:grid-cols-2">
          <SelectField
            label="مسیر تسویه (الزامی)"
            value={rail}
            onChange={setRail}
            options={[
              { value: '', label: 'انتخاب کنید' },
              ...Object.entries(RAIL).map(([k, v]) => ({ value: k, label: v })),
            ]}
          />
          <TextField
            label="کد رهگیری بانکی"
            value={tracking}
            onChange={setTracking}
            ltr
            description="حروف لاتین، رقم و . _ / - (حداکثر ۶۴)"
          />
        </div>
      )}
    </CommandDialog>
  );
}

export function TomanWithdrawalsPage() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<{ w: TomanWithdrawal; action: Action } | null>(null);
  const q = new URLSearchParams({ page: String(page) });
  if (status) q.set('status', status);
  const r = useRemote<Paged<TomanWithdrawal>>(`/toman-withdrawals?${q.toString()}`);
  return (
    <AppContainer>
      <PageHeader title="برداشت تومان" />
      <div className="mb-3 max-w-xs">
        <SelectField
          label="وضعیت"
          value={status}
          onChange={(v) => (setStatus(v), setPage(1))}
          options={[
            { value: '', label: 'همه' },
            ...Object.entries(WITHDRAWAL_STATUS).map(([k, v]) => ({ value: k, label: v.label })),
          ]}
        />
      </div>
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-3" data-withdrawal-queue>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {Object.entries(WITHDRAWAL_STATUS).map(([k, v]) => (
                <Counter
                  key={k}
                  label={v.label}
                  value={num(d.counts?.[k])}
                  {...((k === 'SETTLEMENT_UNCERTAIN' || k === 'SECURITY_HOLD') && d.counts?.[k]
                    ? { tone: 'alert' }
                    : {})}
                />
              ))}
            </div>
            <DataTable<TomanWithdrawal>
              label="برداشت‌های تومان"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (w) => <Ltr className="font-mono text-[12px]">{w.ref}</Ltr>,
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (w) => (
                    <UserCell
                      userId={w.userId}
                      name={w.displayName}
                      publicId={w.publicId}
                      tab="toman"
                    />
                  ),
                },
                {
                  id: 'amount',
                  header: 'مبلغ',
                  numeric: true,
                  cell: (w) => <Ltr className="hx-num">{formatAmount(w.amount, 'TOMAN')}</Ltr>,
                },
                {
                  id: 'dest',
                  header: 'مقصد',
                  cell: (w) => (
                    <span className="flex flex-col text-[12px]">
                      {/* Eye → full number after TOTP step-up; each reveal is audited (OD-41). */}
                      <InstrumentReveal
                        userId={w.userId}
                        instrumentId={w.instrumentId}
                        masked={w.destination.masked}
                      />
                      <span className="text-fg-subtle">
                        {w.destination.kind === 'CARD' ? 'کارت' : 'شبا'}
                      </span>
                      {w.firstToInstrument && (
                        <span className="text-accent-text" data-first-destination>
                          اولین برداشت به این مقصد
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'st',
                  header: 'وضعیت',
                  cell: (w) => (
                    <span className="flex flex-col items-start gap-0.5 text-[12px]">
                      <Tone map={WITHDRAWAL_STATUS} code={w.status} />
                      {w.holdReason && <span className="text-fg-subtle">{w.holdReason}</span>}
                      {w.uncertaintyReason && (
                        <span className="text-fg-subtle">{w.uncertaintyReason}</span>
                      )}
                      {w.rail && (
                        <span className="text-fg-subtle">
                          {RAIL[w.rail]}
                          {w.trackingRef && (
                            <>
                              {' · '}
                              <Ltr className="font-mono">{w.trackingRef}</Ltr>
                            </>
                          )}
                        </span>
                      )}
                      {w.rejectReason && <span className="text-fg-subtle">{w.rejectReason}</span>}
                    </span>
                  ),
                },
                {
                  id: 'at',
                  header: 'تأیید کاربر',
                  cell: (w) => (
                    <span className="flex flex-col text-[12px]">
                      <span>{dt(w.confirmedAt ?? w.createdAt)}</span>
                      {w.decidedAt && (
                        <span className="text-fg-subtle">تصمیم: {dt(w.decidedAt)}</span>
                      )}
                    </span>
                  ),
                },
                {
                  id: 'tx',
                  header: 'اسناد',
                  cell: (w) => (
                    <details className="text-[12px]">
                      <summary className="cursor-pointer text-fg-muted">شناسه</summary>
                      <TechCode>reserve: {w.reserveTransactionId ?? '—'}</TechCode>
                      <TechCode>settle: {w.settleTransactionId ?? '—'}</TechCode>
                    </details>
                  ),
                },
                {
                  id: 'act',
                  header: 'عملیات',
                  cell: (w) => {
                    const acts = (Object.keys(ALLOWED) as Action[]).filter((a) =>
                      ALLOWED[a].includes(w.status),
                    );
                    if (acts.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1">
                        {acts.map((a) => (
                          <Button
                            key={a}
                            size="sm"
                            variant={a === 'COMPLETE' ? 'secondary' : 'ghost'}
                            onPress={() => setOpen({ w, action: a })}
                            data-withdrawal-act={`${w.ref}:${a}`}
                          >
                            {WITHDRAWAL_ACTION[a]}
                          </Button>
                        ))}
                      </div>
                    );
                  },
                },
              ]}
              empty={<StateView compact kind="empty" title="برداشتی با این فیلتر نیست" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </div>
        )}
      </Remote>
      {open && (
        <ActionDialog
          w={open.w}
          action={open.action}
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload())}
        />
      )}
    </AppContainer>
  );
}
